import { useEffect, useMemo, useState } from "react";
import { Search, MapPin, Globe, LayoutGrid, Map as MapIcon, Loader2, Plus, Users, Compass, CalendarClock } from "lucide-react";
import { toast } from "sonner";
import AppLayout, { PageHeader } from "@/components/app/AppLayout";
import { fmtNumber } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { travelApi, PLACE_CATEGORIES } from "@/lib/travelApi";
import MapView from "@/components/maps/MapView";
import PlaceDetailModal from "@/components/travel/PlaceDetailModal";
import ExperienceCard from "@/components/travel/ExperienceCard";
import CreateExperienceModal from "@/components/travel/CreateExperienceModal";
import { TripPlannerPanel } from "@/pages/MyPlans";

export default function Travel() {
  const { user } = useAuth();
  const [tab, setTab] = useState("destinations"); // destinations | community | plans
  const [shareFromPlace, setShareFromPlace] = useState(null);

  const requestShareFromPlace = (place) => {
    if (!user) {
      toast.error("Inicia sesión para compartir tu experiencia");
      return;
    }
    setShareFromPlace(place);
    setTab("community");
  };

  return (
    <AppLayout hideRightRail>
      <PageHeader
        kicker="◊ Viajes"
        title="Viajes."
        subtitle="Descubrí destinos, compartí experiencias reales y armá tu propio cronograma de viaje."
      />

      {/* Tabs */}
      <div className="flex gap-2 mb-5 border-b hairline overflow-x-auto no-scrollbar" data-testid="travel-tabs">
        <TabBtn active={tab === "destinations"} onClick={() => setTab("destinations")} testid="travel-tab-destinations">
          <Compass className="w-4 h-4" /> Destinos
        </TabBtn>
        <TabBtn active={tab === "community"} onClick={() => setTab("community")} testid="travel-tab-community">
          <Users className="w-4 h-4" /> Comunidad
        </TabBtn>
        <TabBtn active={tab === "plans"} onClick={() => setTab("plans")} testid="travel-tab-plans">
          <CalendarClock className="w-4 h-4" /> Mis planes
        </TabBtn>
      </div>

      {tab === "destinations" && (
        <DestinationsTab onShareExperience={requestShareFromPlace} />
      )}
      {tab === "community" && (
        <CommunityTab
          user={user}
          initialPlace={shareFromPlace}
          onConsumeInitialPlace={() => setShareFromPlace(null)}
        />
      )}
      {tab === "plans" && (
        <TripPlannerPanel embedded />
      )}
    </AppLayout>
  );
}

function TabBtn({ active, children, onClick, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm border-b-2 -mb-[1px] transition ${
        active ? "border-coral text-coral font-medium" : "border-transparent text-ink-mid hover:text-ink"
      }`}
    >
      {children}
    </button>
  );
}

function ToggleBtn({ active, onClick, children, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`text-xs px-3 py-1.5 rounded-full border hairline transition inline-flex items-center gap-1.5 ${
        active ? "bg-ink text-cream border-ink" : "bg-cream-card hover:border-coral"
      }`}
    >
      {children}
    </button>
  );
}

/* ============================================================
   TAB 1 — DESTINATIONS
============================================================ */
function DestinationsTab({ onShareExperience }) {
  const [items, setItems] = useState([]);
  const [view, setView] = useState("list");
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [country, setCountry] = useState("");
  const [cat, setCat] = useState("");
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    setLoading(true);
    travelApi
      .listPlaces({ q, country, category: cat })
      .then((d) => setItems(d || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [q, country, cat]);

  const mapItems = useMemo(
    () => items.map((p) => ({ id: p.id, lat: p.lat, lng: p.lng, title: p.name, place: p })),
    [items]
  );

  return (
    <>
      {/* Filters */}
      <div className="flex flex-wrap gap-2 mb-4">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-mid" />
          <input
            data-testid="travel-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar destino…"
            className="w-full bg-cream-card border hairline rounded-full pl-10 pr-4 py-2 text-sm outline-none focus:border-coral"
          />
        </div>
        <input
          data-testid="travel-country"
          value={country}
          onChange={(e) => setCountry(e.target.value)}
          placeholder="País"
          className="bg-cream-card border hairline rounded-full px-3 py-2 text-sm outline-none focus:border-coral w-36"
        />
        <select
          data-testid="travel-category"
          value={cat}
          onChange={(e) => setCat(e.target.value)}
          className="bg-cream-card border hairline rounded-full px-3 py-2 text-sm outline-none"
        >
          {PLACE_CATEGORIES.map((c) => (
            <option key={c.id} value={c.id}>{c.label}</option>
          ))}
        </select>

        <div className="ml-auto flex gap-2">
          <ToggleBtn active={view === "list"} onClick={() => setView("list")} testid="travel-view-list">
            <LayoutGrid className="w-3.5 h-3.5" /> Lista
          </ToggleBtn>
          <ToggleBtn active={view === "map"} onClick={() => setView("map")} testid="travel-view-map">
            <MapIcon className="w-3.5 h-3.5" /> Mapa
          </ToggleBtn>
        </div>
      </div>

      {/* Map */}
      {view === "map" && (
        <MapView
          items={mapItems}
          height="70vh"
          testid="travel-map"
          onMarkerClick={(it) => setSelected(it.place)}
          renderPopup={(it) => (
            <div className="text-sm">
              {it.place?.image && <img src={it.place.image} alt={it.title} className="w-40 h-24 object-cover rounded-lg mb-2" />}
              <p className="font-medium">{it.title}</p>
              <p className="text-[11px] font-mono text-coral mb-1">{it.place?.category}</p>
              <p className="text-[11px] text-ink-mid">{it.place?.city}, {it.place?.country}</p>
              <button
                onClick={() => setSelected(it.place)}
                className="text-xs text-coral hover:underline mt-1"
              >
                Ver detalles →
              </button>
            </div>
          )}
        />
      )}

      {/* List */}
      {view === "list" && (
        <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="travel-grid">
          {loading && (
            <p className="col-span-full text-center text-sm text-ink-mid py-12 inline-flex items-center justify-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" /> Cargando destinos…
            </p>
          )}
          {!loading && items.length === 0 && (
            <p className="col-span-full text-center text-sm text-ink-mid py-12">No hay destinos para esta búsqueda.</p>
          )}
          {!loading && items.map((p) => (
            <button
              key={p.id}
              data-testid={`place-${p.id}`}
              onClick={() => setSelected(p)}
              className="group text-left border hairline bg-cream-card rounded-2xl overflow-hidden hover:shadow-lg hover:border-coral transition"
            >
              <div className="aspect-video bg-cream-deep relative overflow-hidden">
                {p.image && (
                  <img
                    src={p.image}
                    alt={p.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                  />
                )}
                {p.cost_min_usd != null && (
                  <span className="absolute bottom-2 left-2 bg-cream/90 backdrop-blur-sm text-ink text-[10px] font-mono px-2 py-0.5 rounded-full">
                    ${p.cost_min_usd}–${p.cost_max_usd}/día
                  </span>
                )}
              </div>
              <div className="p-4">
                <p className="font-mono text-[9px] uppercase tracking-wider text-coral mb-1">{p.category}</p>
                <h3 className="font-serif text-xl tracking-tight">{p.name}</h3>
                <p className="text-[11px] text-ink-mid font-mono mt-0.5 inline-flex items-center gap-1">
                  <MapPin className="w-3 h-3" /> {p.city ? `${p.city}, ` : ""}{p.country}
                </p>
                <p className="text-xs text-ink-mid mt-2 line-clamp-2">{p.description}</p>
                <p className="text-[11px] text-ink-mid mt-2 inline-flex items-center gap-1">
                  <Globe className="w-3 h-3" /> {fmtNumber(p.travelers || 0)} viajeros han estado
                </p>
              </div>
            </button>
          ))}
        </section>
      )}

      {/* Detail modal */}
      {selected && (
        <PlaceDetailModal
          place={selected}
          onClose={() => setSelected(null)}
          onShareExperience={(p) => {
            setSelected(null);
            onShareExperience?.(p);
          }}
        />
      )}
    </>
  );
}

/* ============================================================
   TAB 2 — COMMUNITY (Travel Experiences)
============================================================ */
function CommunityTab({ user, initialPlace, onConsumeInitialPlace }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [country, setCountry] = useState("");
  const [showCreate, setShowCreate] = useState(false);

  const reload = () => {
    setLoading(true);
    travelApi
      .listExperiences({ q, country })
      .then((d) => setItems(d || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  };

  useEffect(reload, [q, country]);

  // Auto-open create modal when arriving with a pre-selected place
  useEffect(() => {
    if (initialPlace) setShowCreate(true);
  }, [initialPlace]);

  const onCreated = (exp) => setItems((arr) => [exp, ...arr]);
  const onDelete = (eid) => setItems((arr) => arr.filter((e) => e.id !== eid));

  const closeCreate = () => {
    setShowCreate(false);
    onConsumeInitialPlace?.();
  };

  const handleNewClick = () => {
    if (!user) {
      toast.error("Inicia sesión para compartir tu experiencia");
      return;
    }
    setShowCreate(true);
  };

  return (
    <>
      <div className="flex flex-wrap gap-2 mb-4">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-mid" />
          <input
            data-testid="exp-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar experiencias…"
            className="w-full bg-cream-card border hairline rounded-full pl-10 pr-4 py-2 text-sm outline-none focus:border-coral"
          />
        </div>
        <input
          data-testid="exp-country"
          value={country}
          onChange={(e) => setCountry(e.target.value)}
          placeholder="País"
          className="bg-cream-card border hairline rounded-full px-3 py-2 text-sm outline-none focus:border-coral w-36"
        />
        <button
          data-testid="exp-new-btn"
          onClick={handleNewClick}
          className="ml-auto inline-flex items-center gap-1.5 bg-coral text-white text-xs px-4 py-2 rounded-full hover:bg-coral/90 transition"
        >
          <Plus className="w-3.5 h-3.5" /> Compartir experiencia
        </button>
      </div>

      <section className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="exp-grid">
        {loading && (
          <p className="col-span-full text-center text-sm text-ink-mid py-12 inline-flex items-center justify-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Cargando experiencias…
          </p>
        )}
        {!loading && items.length === 0 && (
          <p className="col-span-full text-center text-sm text-ink-mid py-12" data-testid="exp-empty">
            Aún no hay experiencias para esta búsqueda. ¿Te animás a publicar la primera?
          </p>
        )}
        {!loading && items.map((exp) => (
          <ExperienceCard key={exp.id} exp={exp} currentUser={user} onDelete={onDelete} />
        ))}
      </section>

      <CreateExperienceModal
        open={showCreate}
        onClose={closeCreate}
        onCreated={onCreated}
        initialPlace={initialPlace}
      />
    </>
  );
}
